/**
 * Промежуток-101%: ModalWindow → adapter поверх единого UnvUI/Modal.
 *
 * Раньше существовали 4 параллельных модальных стека (MUI ModalWindow, UnvDialog/UnvModal,
 * UnvUI/Modal, прямой MUI <Modal>). Теперь все потребители ModalWindow работают через
 * UnvUI/Modal — один portal, один scroll-lock, один focus-trap, один z-index.
 *
 * Старый API сохранён для обратной совместимости:
 *   <ModalWindow openModal={…} setOpenModal={…} modalTitle="…" modalText="…" width="600px" />
 */
import React from 'react'
import { useTranslation } from 'react-i18next'
import UnvModal from 'components/UnvUI/Modal'

const widthToSize = (w) => {
  if (!w || w === 'unset') return 'md'
  if (typeof w === 'string') {
    const px = parseInt(w, 10)
    if (Number.isFinite(px)) {
      if (px <= 360) return 'xs'
      if (px <= 480) return 'sm'
      if (px <= 720) return 'md'
      if (px <= 960) return 'lg'
      return 'xl'
    }
  }
  return 'md'
}

export default function ModalWindow({
  modalText,
  modalTitle,
  children,
  width,
  openModal,
  setOpenModal,
  closeOnBackdrop = true,
  hasUnsavedChanges = false,
  showClose = true,
}) {
  const { t } = useTranslation('common')

  const safeClose = () => {
    if (hasUnsavedChanges) {
      const ok = window.confirm(
        t('common.unsaved_confirm', 'Закрыть без сохранения? Введённые данные будут потеряны.'),
      )
      if (!ok) return
    }
    setOpenModal(false)
  }

  // modalTitle может быть как строкой, так и React-нодой (старая совместимость).
  const titleNode = (() => {
    if (modalTitle == null) return undefined
    if (typeof modalTitle === 'string') return modalTitle
    // React-нода — рендерим её "как заголовок", но UnvModal принимает строку.
    // В этом случае передаём заголовок как часть children, не через title.
    return undefined
  })()

  const renderedTitleNode =
    modalTitle && typeof modalTitle !== 'string' ? (
      <div style={{ marginBottom: 8 }}>{modalTitle}</div>
    ) : null

  return (
    <UnvModal
      open={!!openModal}
      onClose={safeClose}
      title={titleNode}
      size={widthToSize(width)}
      closeOnOverlay={closeOnBackdrop}
      showClose={showClose}
    >
      {renderedTitleNode}
      {modalText ? (
        <h3
          id="modal-description"
          style={{
            margin: 0,
            marginBottom: 14,
            fontSize: 15,
            fontWeight: 400,
            color: 'var(--color-text-soft)',
          }}
        >
          {modalText}
        </h3>
      ) : null}
      {children}
    </UnvModal>
  )
}
